import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { isBrowser } from '@/utils/environmentSafety';
import { LANGUAGES_VERSION } from '@/constants/languages';
import { getCachedTranslation, queueForTranslation, setTranslationListener } from '@/services/aiTranslationService';
import { supabase } from '@/integrations/supabase/client';

// English is the only locale that must be present synchronously — it is the
// fallback for every other language and the default for most visitors. It is
// SPLIT IN TWO (2026-09-06, see scripts/i18n-split-en.mjs):
//
//   en.core.json  the ~168 kB reachable from public routes — statically
//                 imported, so `t()` is synchronous from the first paint.
//   en.rest.json  the ~209 kB only an authenticated surface can reach —
//                 dynamically imported, so an anonymous visitor to the landing
//                 page never downloads it.
//
// Both files are BUILD ARTIFACTS regenerated from the authored
// `src/i18n/locales/en.json` by `npm run i18n:split` (wired into prebuild).
// Edit en.json; never edit the generated pair.
import enCore from '@/i18n/generated/en.core.json';
// ~1 kB list of the top-level namespaces that live in `en.rest.json`. Ships in
// the eager chunk so `resolveTranslation` can distinguish "deferred" from
// "does not exist" without a network round trip — see its use below.
import enRestNamespaces from '@/i18n/generated/en.rest.namespaces.json';

const EN_REST_NAMESPACES = new Set(enRestNamespaces as string[]);

// The curated dictionary carries all six non-English translations for every
// entry, so it is ~25 kB of Perso-Arabic/Arabic text that an English user can
// never consult: BOTH call sites below are guarded by `language !== 'en'`.
// Statically imported it landed in the eager `i18n` chunk on every page load,
// including the anonymous landing page. It is now loaded alongside a locale in
// `ensureLocaleLoaded`, which always runs before any non-English render, so by
// the time a lookup happens the dictionary is in place. If it somehow is not,
// `dictionaryLookup` is null and the call sites fall through to the AI cache —
// which is exactly what they already do on a dictionary miss.
type DictionaryLookup = (lang: string, englishText: string) => string | undefined;
let dictionaryLookup: DictionaryLookup | null = null;
let dictionaryLoad: Promise<void> | null = null;

function ensureDictionaryLoaded(): Promise<void> {
  if (dictionaryLookup) return Promise.resolve();
  if (dictionaryLoad) return dictionaryLoad;
  dictionaryLoad = import('@/i18n/translationDictionary')
    .then(m => {
      dictionaryLookup = m.getDictionaryTranslation;
    })
    .catch(() => {
      // Never crash a language switch over the optional curated dictionary.
    });
  return dictionaryLoad;
}

export type Language = 'en' | 'ur' | 'sd' | 'skr' | 'bal' | 'ps' | 'ar';
export type SupportedLanguage = Language;

interface LanguageContextType {
  language: Language;
  changeLanguage: (lang: Language) => void;
  refreshAccountLanguage: () => Promise<void>;
  t: (key: string) => string;
  /**
   * True when `key` resolves to real copy in the active locale or in English.
   *
   * Needed because `t()` NEVER signals failure: for an unknown key it returns
   * `humanizeKey(key)` — "legal.trial.purpose.title" becomes "Title" — which is
   * a truthy string. So the widespread `t(key) || fallback` idiom can never
   * reach its fallback, and a missing key renders a mangled key fragment
   * instead of the author's copy. Test with this before falling back.
   */
  hasTranslation: (key: string) => boolean;
  translateDynamic: (text: string | null | undefined) => string;
  isRTL: boolean;
}

// Locale shape: most keys are flat strings (e.g. "about.badge"), but some
// keys are nested objects (e.g. admin: { actions: "..." }). Both shapes
// coexist; resolveTranslation() handles flat-first, then dot-path traversal.
type LocaleDict = Record<string, string | Record<string, any>>;

// CHANGED: Module-level locale registry. English is pre-populated; the other
// 6 are inserted the first time that language is activated (load-once).
// A COPY, not the imported object: `ensureEnglishFullLoaded` merges the
// deferred slice into it with Object.assign, and mutating a module import is
// both illegal under ESM live bindings and hostile to anything else importing
// the JSON.
const loadedLocales: Partial<Record<Language, LocaleDict>> = { en: { ...(enCore as LocaleDict) } };

// ---------------------------------------------------------------------------
// Deferred English slice
// ---------------------------------------------------------------------------

/**
 * Load the authenticated-surface half of English and merge it into `en`.
 *
 * Called from three places, which between them close every window in which a
 * user could see an unresolved key:
 *
 *  1. `useEnglishFullReady()` — the `/dashboard` shell holds its existing
 *     spinner until this resolves. The dashboard route chunk is an order of
 *     magnitude larger than this slice and downloads in parallel, so the gate
 *     costs nothing measurable.
 *  2. `ensureLocaleLoaded()` — a non-English locale needs the FULL English
 *     dictionary behind it, because English is the fallback for any key its
 *     own translation is missing.
 *  3. `resolveTranslation()` — a self-healing last resort. If some surface not
 *     seen by the split scan asks for a deferred key, the fetch starts there
 *     and the provider re-renders with the real copy when it lands.
 *
 * Never rejects: a failed fetch leaves the humanised-key fallback in place
 * rather than taking the app down, and is retried on the next miss.
 */
let enRestLoaded = false;
let enRestLoad: Promise<void> | null = null;
const enRestListeners = new Set<() => void>();

export function isEnglishFullLoaded(): boolean {
  return enRestLoaded;
}

export function ensureEnglishFullLoaded(): Promise<void> {
  if (enRestLoaded) return Promise.resolve();
  if (enRestLoad) return enRestLoad;

  enRestLoad = import('@/i18n/generated/en.rest.json')
    .then((m) => {
      Object.assign(loadedLocales.en as LocaleDict, m.default as LocaleDict);
      enRestLoaded = true;
      // Misses recorded while the slice was absent are now wrong. Every
      // language's cache is cleared, not just English: a non-English lookup
      // caches the English fallback it resolved to.
      (Object.keys(translationCache) as Language[]).forEach((l) => translationCache[l].clear());
      enRestListeners.forEach((fn) => { try { fn(); } catch { /* one listener must not stop the rest */ } });
    })
    .catch(() => {
      enRestLoad = null; // allow a later miss to retry
    });

  return enRestLoad;
}

// CHANGED: Static set used for language validation (replaces `translations[saved]` checks).
const VALID_LANGUAGES = new Set<Language>(['en', 'ur', 'sd', 'skr', 'bal', 'ps', 'ar']);

// CHANGED: Dynamic import map — Vite will code-split each JSON into its own chunk.
const LOCALE_LOADERS: Record<Exclude<Language, 'en'>, () => Promise<LocaleDict>> = {
  ur:  () => import('@/i18n/locales/ur.json').then(m => m.default as LocaleDict),
  sd:  () => import('@/i18n/locales/sd.json').then(m => m.default as LocaleDict),
  skr: () => import('@/i18n/locales/skr.json').then(m => m.default as LocaleDict),
  bal: () => import('@/i18n/locales/bal.json').then(m => m.default as LocaleDict),
  ps:  () => import('@/i18n/locales/ps.json').then(m => m.default as LocaleDict),
  ar:  () => import('@/i18n/locales/ar.json').then(m => m.default as LocaleDict),
};

// In-flight loads, keyed by language. Guarantees that a locale is fetched at
// most once even if the idle prefetch and the per-switch effect race.
const inFlightLocaleLoads: Partial<Record<Language, Promise<void>>> = {};

/**
 * The ONE path by which a locale dictionary enters `loadedLocales`.
 * Both the per-switch loader effect and the idle prefetch call this, so
 * "how a locale gets registered" (including the translationCache invalidation
 * that stops stale English fallbacks being served permanently) lives in
 * exactly one place.
 *
 * Never rejects — a failed locale fetch silently leaves English in place.
 */
function ensureLocaleLoaded(lang: Language): Promise<void> {
  if (lang === 'en' || loadedLocales[lang]) return Promise.resolve();

  const existing = inFlightLocaleLoads[lang];
  if (existing) return existing;

  const loader = LOCALE_LOADERS[lang as Exclude<Language, 'en'>];
  if (!loader) return Promise.resolve();

  // The curated dictionary is fetched alongside the locale — same trigger,
  // same "this user is going non-English" signal — so it is ready before the
  // first lookup without ever loading for an English-only session.
  // The full English dictionary is fetched alongside, because English is the
  // fallback for every key this locale does not translate — with only the core
  // slice present, a missing translation would fall back to a humanised key
  // instead of the English sentence.
  const load = Promise.all([loader(), ensureDictionaryLoaded(), ensureEnglishFullLoaded()])
    .then(([dict]) => {
      loadedLocales[lang] = dict;
      // Invalidate the translation cache for this language so we don't serve
      // stale English-fallback entries cached during the loading window.
      translationCache[lang].clear();
    })
    .catch(() => {
      // On fetch failure silently fall through to English. Do not crash.
    })
    .finally(() => {
      delete inFlightLocaleLoads[lang];
    });

  inFlightLocaleLoads[lang] = load;
  return load;
}

/**
 * Warm every non-English locale, on evidence the user is about to pick one.
 *
 * REPLACES A BLANKET IDLE PREFETCH (2026-09-06). The previous version warmed
 * all six locales via `requestIdleCallback` on EVERY page load, for every
 * visitor. Measured on the built bundle, an anonymous English visitor to `/`
 * downloaded 4,769.7 kB of JavaScript, of which **2,709.7 kB — 57% — was those
 * six locale chunks**, on a platform whose users are overwhelmingly on metered
 * Pakistani mobile data and whose own `user_preferences` shows 285 of 297
 * accounts on English.
 *
 * The goal it was serving — a first language switch that feels as instant as
 * repeat switches — is kept, and met more cheaply: opening the switcher is a
 * far better predictor of an imminent switch than loading a page is, and it
 * still buys the several hundred milliseconds a user spends reading the menu
 * before choosing. On touch, where there is no hover, the trigger's own
 * pointer/open event fires before the menu paints, so the head start survives.
 *
 * Sequential by design, so warming never opens six parallel requests that
 * compete with traffic the user actually asked for. Idempotent and safe to
 * call on every open — `ensureLocaleLoaded` short-circuits an already-loaded
 * locale and de-dupes in-flight ones.
 */
export function warmAllLocales(): void {
  if (!isBrowser) return;
  // Respect an explicit data-saver opt-in: ~450 kB per locale is real money on
  // a metered connection. These users load their locale on switch instead.
  if ((navigator as any)?.connection?.saveData === true) return;

  (Object.keys(LOCALE_LOADERS) as Exclude<Language, 'en'>[]).reduce(
    (chain, lang) => chain.then(() => ensureLocaleLoaded(lang)),
    Promise.resolve() as Promise<void>,
  );
}

const RTL_LANGUAGES: Language[] = ['ur', 'sd', 'skr', 'bal', 'ps', 'ar'];

const FONT_MAP: Record<string, string> = {
  ur: 'font-urdu',
  sd: 'font-sindhi',
  skr: 'font-saraiki',
  bal: 'font-balochi',
  ps: 'font-pashto',
  ar: 'font-arabic'
};

const ALL_FONT_CLASSES = Object.values(FONT_MAP);

const PERSONA_SEGMENT_MAP: Record<string, string> = {
  'job-seeker': 'jobSeeker',
  'job_seeker': 'jobSeeker',
  'individual-employer': 'individualEmployer',
  'individual_employer': 'individualEmployer',
  'business-employer': 'businessEmployer',
  'business_employer': 'businessEmployer',
  'service-provider': 'serviceProvider',
  'service_provider': 'serviceProvider',
  'agency-provider': 'agencyProvider',
  'agency_provider': 'agencyProvider',
  agency: 'agencyProvider',
  provider: 'serviceProvider',
};

// CHANGED: Validates against VALID_LANGUAGES set instead of checking translations object.
function getInitialLanguage(): Language {
  if (!isBrowser) return 'en';
  try {
    const saved = localStorage.getItem('behtari_language') as Language;
    if (saved && VALID_LANGUAGES.has(saved)) return saved;
  } catch { /* noop */ }
  return 'en';
}

const getTranslationCandidates = (key: string): string[] => {
  const candidates = new Set<string>([key]);

  if (key.endsWith('.')) {
    candidates.add(`${key}user`);
  }

  if (key.startsWith('persona.') || key.startsWith('personas.')) {
    const [prefix, rawSegment = ''] = key.split('.');
    const segment = rawSegment.trim();

    if (!segment) {
      candidates.add(prefix === 'persona' ? 'persona.user' : 'personas.jobSeeker');
    }

    const mappedSegment = PERSONA_SEGMENT_MAP[segment];
    if (mappedSegment) {
      candidates.add(`${prefix}.${mappedSegment}`);
    }

    if (prefix === 'persona') candidates.add(key.replace('persona.', 'personas.'));
    if (prefix === 'personas') candidates.add(key.replace('personas.', 'persona.'));
  }

  return Array.from(candidates);
};

// CHANGED: Uses loadedLocales instead of the old static `translations` object.
const resolveTranslation = (lang: Language, key: string): string | undefined => {
  const dict = loadedLocales[lang];
  if (!dict) return undefined;

  // 1. Flat key match (preserves existing "about.badge" style)
  const flat = dict[key];
  if (typeof flat === 'string') return flat;

  // 2. Dot-path traversal into nested objects (e.g. "admin.actions")
  if (key.includes('.')) {
    const parts = key.split('.');
    let cursor: any = dict;
    for (const part of parts) {
      if (cursor && typeof cursor === 'object' && part in cursor) {
        cursor = cursor[part];
      } else {
        cursor = undefined;
        break;
      }
    }
    if (typeof cursor === 'string') return cursor;
  }

  // Self-healing last resort for the deferred English slice — but ONLY for a
  // key whose namespace actually lives there.
  //
  // The namespace test is load-bearing, not a micro-optimisation. `t()` is
  // called with keys that exist nowhere in en.json (which is why
  // `hasTranslation` exists at all), and firing on any unresolved key made a
  // single such key on the landing page pull the whole 209 kB slice down for
  // every anonymous visitor. That was measured on the first build of the
  // split, and it silently undid the entire saving.
  if (!enRestLoaded && key.includes('.') && EN_REST_NAMESPACES.has(key.split('.')[0])) {
    void ensureEnglishFullLoaded();
  }

  return undefined;
};

// =====================================================================
// PERFORMANCE: Per-language translation cache (module-level, persists
// across renders). Hot-path t() becomes O(1) Map.get() after first hit.
// Cleared automatically when language changes or locale finishes loading.
// =====================================================================
const translationCache: Record<Language, Map<string, string>> = {
  en: new Map(),
  ur: new Map(),
  sd: new Map(),
  skr: new Map(),
  bal: new Map(),
  ps: new Map(),
  ar: new Map(),
};

// Sentinel for cached "not found" so we don't re-resolve missing keys repeatedly
const NOT_FOUND = '\0__NF__';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  // CHANGED: tracks whether the active locale JSON has finished loading.
  // English is always ready; other locales need a dynamic import first.
  const [localeReady, setLocaleReady] = useState<boolean>(getInitialLanguage() === 'en');
  // Counter to force re-renders when AI translations arrive
  const [, setAiTranslationTick] = useState(0);
  const hasLoadedAccountLang = useRef(false);

  // Re-render once the deferred English slice lands, so any humanised-key
  // fallback rendered in the meantime is replaced by the real copy. Reuses the
  // AI-translation tick — same purpose, same mechanism, no second counter.
  useEffect(() => {
    if (enRestLoaded) return;
    const onLoaded = () => setAiTranslationTick((n) => n + 1);
    enRestListeners.add(onLoaded);
    return () => { enRestListeners.delete(onLoaded); };
  }, []);

  const isRTL = RTL_LANGUAGES.includes(language);

  // CHANGED: Effect that dynamically loads the non-English locale JSON the
  // first time it is needed. Subsequent activations of the same language hit
  // the already-populated loadedLocales and resolve synchronously.
  useEffect(() => {
    if (language === 'en' || loadedLocales[language]) {
      setLocaleReady(true);
      return;
    }
    setLocaleReady(false);

    let cancelled = false;
    // ensureLocaleLoaded owns the fetch + registration + cache invalidation and
    // never rejects; this effect only owns the localeReady flag.
    ensureLocaleLoaded(language).then(() => {
      if (!cancelled) setLocaleReady(true);
    });

    return () => { cancelled = true; };
  }, [language]);

  // The blanket idle prefetch that used to live here was REMOVED on 2026-09-06.
  // See `warmAllLocales` below for what replaced it and why. Warming is now
  // driven by the language switcher opening, not by every page load.

  // Register AI translation listener — invalidate cache for current language
  // so newly arrived AI translations are picked up on next render.
  useEffect(() => {
    setTranslationListener(() => {
      // Clear only the current language cache; English is static
      if (language !== 'en') translationCache[language].clear();
      setAiTranslationTick(prev => prev + 1);
    });
    return () => setTranslationListener(() => {});
  }, [language]);

  const applyLanguage = useCallback((lang: Language) => {
    setLanguage(lang);
    if (isBrowser) {
      try {
        localStorage.setItem('behtari_language', lang);
        const isNewRTL = RTL_LANGUAGES.includes(lang);
        document.documentElement.dir = isNewRTL ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        const body = document.body;
        body.classList.remove(...ALL_FONT_CLASSES);
        if (FONT_MAP[lang]) body.classList.add(FONT_MAP[lang]);
      } catch { /* noop */ }
    }
  }, []);

  // Public method: load account-level language preference. Called once by
  // UserContext after profile load. Replaces the duplicate auth listener
  // that previously fired user_preferences queries on every auth event
  // (including null user during sign-out, which logged a console error).
  const refreshAccountLanguage = useCallback(async () => {
    if (hasLoadedAccountLang.current) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      hasLoadedAccountLang.current = true;
      const { data } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();
      const uiLang = (data as any)?.ui_language;
      // CHANGED: uses VALID_LANGUAGES set for validation.
      if (uiLang && VALID_LANGUAGES.has(uiLang as Language) && uiLang !== language) {
        applyLanguage(uiLang as Language);
      }
    } catch { /* noop */ }
  }, [language, applyLanguage]);

  // One-shot load on mount if a session already exists (page refresh path)
  useEffect(() => {
    refreshAccountLanguage();
    // Reset the guard whenever the user signs out, so a subsequent
    // sign-in re-loads account language. We don't query here.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        hasLoadedAccountLang.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, [refreshAccountLanguage]);

  const changeLanguage = useCallback((newLanguage: Language) => {
    applyLanguage(newLanguage);

    // Persist to account level (fire-and-forget)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) {
        supabase
          .from('user_preferences')
          .upsert(
            { user_id: session.user.id, ui_language: newLanguage, updated_at: new Date().toISOString() },
            { onConflict: 'user_id' }
          )
          .then(() => { /* noop */ });
      }
    });
  }, [applyLanguage]);

  const t = useCallback((key: string): string => {
    // CHANGED: While the active locale JSON is still loading (brief window,
    // typically <100ms), silently serve English. We do NOT cache these
    // English results under the target language key — the cache for that
    // language is cleared once the locale arrives (see the loading effect).
    if (!localeReady && language !== 'en') {
      const enResult = resolveTranslation('en', key);
      if (enResult) return enResult;
      const tail = key.split('.').pop() || key;
      return tail.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim() || key;
    }

    // === HOT PATH: O(1) cache lookup ===
    const cache = translationCache[language];
    const cached = cache.get(key);
    if (cached !== undefined) {
      return cached === NOT_FOUND ? key : cached;
    }

    // === SLOW PATH: resolve & cache ===
    const candidates = getTranslationCandidates(key);

    // 1. Static JSON translations for current language
    for (const candidate of candidates) {
      const localized = resolveTranslation(language, candidate);
      if (localized) {
        cache.set(key, localized);
        return localized;
      }
    }

    // 2. English fallback
    let englishText: string | undefined;
    for (const candidate of candidates) {
      englishText = resolveTranslation('en', candidate);
      if (englishText) break;
    }

    // Special persona fallback
    if (!englishText && key.startsWith('persona.')) {
      const personaFallback =
        resolveTranslation(language, 'persona.user') ||
        resolveTranslation('en', 'persona.user') ||
        'User';
      cache.set(key, personaFallback);
      return personaFallback;
    }

    // Humanize raw dot-case keys so missing translations never leak as code into the UI.
    const humanizeKey = (k: string): string => {
      const tail = k.split('.').pop() || k;
      const spaced = tail
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
      if (!spaced) return k;
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };
    const fallback = englishText || humanizeKey(key);

    // 3. For non-English, check dictionary → AI cache → queue
    if (language !== 'en' && englishText) {
      const dictResult = dictionaryLookup?.(language, englishText);
      if (dictResult) {
        cache.set(key, dictResult);
        return dictResult;
      }

      const aiCached = getCachedTranslation(language, englishText);
      if (aiCached) {
        cache.set(key, aiCached);
        return aiCached;
      }

      // Queue for async AI translation — DO NOT cache fallback permanently;
      // we want to pick up the AI result when it arrives (listener clears cache).
      queueForTranslation(language, englishText);
      return fallback;
    }

    // 4. Cache the English fallback / key for current language
    cache.set(key, fallback);
    return fallback;
  }, [language, localeReady]);

  // Mirrors t()'s own resolution order (active locale, then English) but reports
  // whether real copy exists instead of manufacturing a humanized placeholder.
  const hasTranslation = useCallback((key: string): boolean => {
    const candidates = getTranslationCandidates(key);
    for (const candidate of candidates) {
      if (resolveTranslation(language, candidate)) return true;
      if (resolveTranslation('en', candidate)) return true;
    }
    return false;
  }, [language]);

  // Version-based cache invalidation
  useEffect(() => {
    if (!isBrowser) return;
    try {
      const storedVersion = localStorage.getItem('behtari_lang_version');
      if (storedVersion !== LANGUAGES_VERSION) {
        localStorage.setItem('behtari_lang_version', LANGUAGES_VERSION);
        if (storedVersion) {
          window.location.reload();
          return;
        }
      }
    } catch { /* noop */ }
  }, []);

  // Apply direction and font when language changes + clear stale per-language cache
  useEffect(() => {
    if (!isBrowser) return;
    const isNewRTL = RTL_LANGUAGES.includes(language);
    document.documentElement.dir = isNewRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;

    const body = document.body;
    body.classList.remove(...ALL_FONT_CLASSES);
    if (FONT_MAP[language]) {
      body.classList.add(FONT_MAP[language]);
    }
  }, [language]);

  const translateDynamic = useCallback((text: string | null | undefined): string => {
    if (!text) return '';
    if (language === 'en') return text;

    // Check curated dictionary first
    const dictResult = dictionaryLookup?.(language, text);
    if (dictResult) return dictResult;

    const aiCached = getCachedTranslation(language, text);
    if (aiCached) return aiCached;

    queueForTranslation(language, text);
    return text;
  }, [language]);

  const contextValue = React.useMemo(() => ({
    language, changeLanguage, refreshAccountLanguage, t, hasTranslation, translateDynamic, isRTL
  }), [language, changeLanguage, refreshAccountLanguage, t, hasTranslation, translateDynamic, isRTL]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

const fallbackContext: LanguageContextType = {
  language: 'en' as Language,
  changeLanguage: () => {},
  refreshAccountLanguage: async () => {},
  t: (key: string) => key,
  hasTranslation: () => false,
  translateDynamic: (text: string) => text,
  isRTL: false,
};

/**
 * True once the deferred English slice is in place.
 *
 * The `/dashboard` shell holds its spinner on this so an authenticated surface
 * can never render a key the core slice does not carry. It is deliberately a
 * hook rather than a Suspense throw: the shell already owns a spinner, and
 * suspending here would remount the provider tree beneath it.
 */
export const useEnglishFullReady = (maxWaitMs = 3000): boolean => {
  const [ready, setReady] = useState(enRestLoaded);

  useEffect(() => {
    if (enRestLoaded) return;
    let cancelled = false;
    const onLoaded = () => { if (!cancelled) setReady(true); };
    enRestListeners.add(onLoaded);
    void ensureEnglishFullLoaded();

    // THE GATE MUST NEVER BE ABLE TO HANG THE DASHBOARD. On a dead connection
    // or a purged chunk the import can fail or stall indefinitely, and a
    // permanently false flag would reproduce exactly the stuck-spinner class of
    // bug this codebase has already paid for twice. After the deadline the
    // shell renders regardless: the worst case is a humanised key on screen for
    // a moment, which `resolveTranslation`'s self-heal then repairs — strictly
    // better than an app that never loads.
    const timer = window.setTimeout(() => { if (!cancelled) setReady(true); }, maxWaitMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      enRestListeners.delete(onLoaded);
    };
  }, [maxWaitMs]);

  return ready;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Return safe fallback instead of throwing — prevents HMR/lazy-load crashes
    return fallbackContext;
  }
  return context;
};
